import { useEffect, useMemo, useState } from "react";
import { CreditCard, IndianRupee, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import MobileScreenHeader from "../components/MobileScreenHeader";

/** Aggregates payments from subscriptions, feature-requests, boost-orders and listing-credits
 *  into a single unified history — no dedicated backend endpoint required. */

/** Backend uses inconsistent money field names (amount_inr / payable_inr / amount) — normalize. */
const money = (r) => Number(r?.amount_inr ?? r?.payable_inr ?? r?.amount ?? 0);
const OK_STATUSES      = ["success", "captured", "paid", "approved_active", "available"];
const PENDING_STATUSES = ["pending", "approved_pending", "processing", "created"];
export default function MyPayments() {
    const [loading, setLoading]   = useState(true);
    const [subs, setSubs]         = useState([]);
    const [features, setFeatures] = useState([]);
    const [boosts, setBoosts]     = useState([]);
    const [credits, setCredits]   = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const [s, f, b, c] = await Promise.all([
                api.get("/subscriptions/my-plan").then((r) => r.data?.history || []).catch(() => []),
                api.get("/me/feature-requests").then((r) => r.data || []).catch(() => []),
                api.get("/boost/my-orders").then((r) => r.data?.items || r.data || []).catch(() => []),
                api.get("/me/listing-credits").then((r) => r.data?.items || r.data || []).catch(() => []),
            ]);
            setSubs(s); setFeatures(f); setBoosts(b); setCredits(c);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const unified = useMemo(() => {
        const rows = [];
        subs.forEach((s) => rows.push({
            id: `sub-${s.id}`, kind: "Subscription",
            title: `Plan: ${s.plan_id || s.plan || "—"}`,
            amount: money(s), status: s.status || "success",
            at: s.created_at || s.paid_at,
        }));
        features.forEach((f) => rows.push({
            id: `feat-${f.id}`, kind: "Feature Boost",
            title: `Property #${(f.property_id || "").slice(0, 8)}`,
            amount: money(f), status: f.status || "pending",
            at: f.created_at,
        }));
        boosts.forEach((b) => rows.push({
            id: `boost-${b.id}`, kind: "Boost",
            title: b.plan_name || `Boost ${b.plan_id || ""}`,
            amount: money(b), status: b.status || "success",
            at: b.created_at,
        }));
        credits.forEach((c) => rows.push({
            id: `cred-${c.id}`, kind: "Listing Credit",
            title: `Extra listing (₹${money(c) || 99})`,
            amount: money(c) || 99, status: c.status || "available",
            at: c.created_at,
        }));
        return rows.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
    }, [subs, features, boosts, credits]);

    const total = useMemo(() => unified
        .filter((r) => [...OK_STATUSES, ...PENDING_STATUSES].includes((r.status || "").toString()))
        .reduce((sum, r) => sum + Number(r.amount || 0), 0), [unified]);

    const statusIcon = (s) => {
        if (OK_STATUSES.includes(s)) return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
        if (PENDING_STATUSES.includes(s)) return <Clock className="w-4 h-4 text-amber-600" />;
        return <XCircle className="w-4 h-4 text-red-600" />;
    };

    return (
        <div className="min-h-screen bg-[#f6f7fb]" data-testid="my-payments-page">
            <MobileScreenHeader title="My Payments" subtitle="Transaction history" testid="payments-header" />

            {loading ? (
                <div className="flex items-center justify-center py-12 text-[#002657]/60">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
            ) : (
                <>
                    {/* Total spend card */}
                    <div className="px-4 -mt-2">
                        <div
                            className="text-on-image rounded-2xl p-5 shadow-xl"
                            style={{ background: "linear-gradient(135deg, #001a3d 0%, #002657 60%, #003366 100%)", boxShadow: "0 15px 35px -12px rgba(0,15,35,0.55)" }}
                            data-testid="payments-total-card"
                        >
                            <div className="flex items-center justify-between">
                                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#F5E5A5" }}>Total Spent</div>
                                <CreditCard className="w-5 h-5" style={{ color: "#F5E5A5" }} />
                            </div>
                            <div className="mt-2 font-heading font-extrabold text-white text-3xl flex items-baseline">
                                <IndianRupee className="w-6 h-6 mr-0.5" />
                                <span data-testid="payments-total">{total.toLocaleString("en-IN")}</span>
                            </div>
                            <div className="mt-1 text-[11px] text-white/70">{unified.length} transactions</div>
                        </div>
                    </div>

                    <div className="mt-5 px-4 pb-6">
                        <div className="text-[11px] uppercase tracking-widest font-extrabold text-[#8B6712] mb-2">Transactions</div>
                        {unified.length === 0 ? (
                            <div className="rounded-2xl bg-white border border-[#002657]/10 p-6 text-center text-[#002657]/60 text-sm" data-testid="payments-empty">
                                No payments yet.
                            </div>
                        ) : (
                            <div className="space-y-2" data-testid="payments-list">
                                {unified.map((r) => (
                                    <div key={r.id} className="rounded-2xl bg-white border border-[#002657]/10 p-3 flex items-center gap-3" data-testid={`payment-item-${r.id}`}>
                                        <div className="w-9 h-9 rounded-full bg-[#FBF6E0] flex items-center justify-center">
                                            {statusIcon(r.status)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#8B6712]">{r.kind}</div>
                                            <div className="text-[13px] font-bold text-[#002657] truncate">{r.title}</div>
                                            <div className="text-[10px] text-[#002657]/55">{(r.at || "").slice(0, 10)} · {r.status}</div>
                                        </div>
                                        <div className="text-[13px] font-extrabold text-[#8B6712]">₹{Number(r.amount || 0).toLocaleString("en-IN")}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
